<template>
  <div
    class="panel-container"
    v-if="show">
    <!--Header-->
    <div
      class="webapp-header"
      v-if="office.initialized">
      <div class="webapp-title">
        <a
          v-if="currentWebApp"
          @click="selectedPanel = 'webapp-edition'"
          :title="currentWebApp.label">
          {{ currentWebApp.label }}
        </a>
        <span
          v-if="currentWebApp"
          style="font-size: 10px"> - Webapp</span>
      </div>
      <v-spacer />
      <v-btn
        class="action-button"
        slot="activator"
        @click="openDesignManager"
        icon
        small>
        <v-icon>apps</v-icon>
      </v-btn>
      <v-menu
        bottom
        class="account-menu"
        lazy
        absolute
        offset-y
        :z-index="zIndex"
        v-model="profileMenuOpen">
        <v-btn
          class="action-button"
          slot="activator"
          icon
          small>
          <v-icon>account_circle</v-icon>
        </v-btn>
        <we-profile-menu class="profile-menu" />
      </v-menu>
    </div>
    <!--Content-->
    <div class="content-panel">
      <Home v-if="selectedPanel === 'home'" />
      <DesignPanels
        v-if="selectedPanel === 'template'"
        :data-source="templateDataSource" />
      <DesignPanels
        v-if="selectedPanel === 'widget'"
        :data-source="widgetDataSource" />
      <we-chat v-if="selectedPanel === 'chat'" />
      <we-mattermost v-if="selectedPanel === 'collaboration'" />
      <WebAppSelection v-if="selectedPanel === 'webapp-selection'" />
      <WebAppEdition v-if="selectedPanel === 'webapp-edition'" />
      <Suggestions v-if="selectedPanel === 'suggestions'" />
    </div>
    <!--Menu-->
    <div class="right-panel">
      <div
        class="menu-item"
        :class="{ selected: item.id === selectedPanel, [item.className]: true, disabled: item.disabled && item.disabled(context) }"
        v-for="item in filteredMenu"
        :key="item.id"
        @click="onPanelClick(item)">
        <v-icon :class="item.iconClassName">
          {{ item.icon }}
        </v-icon>
        <div class="text-no-wrap">
          {{ item.label }}
        </div>
      </div>
    </div>
    <OfficeBusyIndicator />
  </div>
  <div v-else>
    {{ $t('wdm.views.designPanel.errors.officeNotInitialized', 'Office not initialized.') }}
  </div>
</template>

<script>
import { isEqual } from 'lodash'
import { mapState, mapActions } from 'vuex'
import { templateDataSource, widgetDataSource, webAppDataSource } from 'server-model'
import OfficeBusyIndicator from './OfficeBusyIndicator'

// const DEBUG = process.env.NODE_ENV !== 'production'

export default {
  components: {
    DesignPanels: () => import(/* webpackChunkName: "DesignPanels" */ './DesignPanels'),
    WebAppSelection: () => import(/* webpackChunkName: "WebAppSelection" */ './WebAppSelection'),
    WebAppEdition: () => import(/* webpackChunkName: "WebAppEdition" */ './WebAppEdition'),
    Suggestions: () => import(/* webpackChunkName: "Suggestions" */ './Suggestions'),
    Home: () => import(/* webpackChunkName: "Home" */ './Home'),
    OfficeBusyIndicator
  },
  data () {
    return {
      templateDataSource: templateDataSource,
      widgetDataSource: widgetDataSource,
      profileMenuOpen: false,
      zIndex: 9999
    }
  },
  computed: {
    ...mapState('office', {
      office: state => state
    }),
    ...mapState('wdm', [
      'currentPanel',
      'currentWebApp'
    ]),
    ...mapState('schema-view', [
      'editedDefinition'
    ]),
    ...mapState('authentication', [
      'token',
      'instance',
      'defaultRoute'
    ]),
    menu () {
      return [
        {
          id: 'home',
          label: this.$t('wdm.views.designPanel.menuItem.home', 'Home'),
          icon: 'fas fa-home'
        },
        {
          id: 'save',
          label: this.$t('wdm.views.designPanel.menuItem.save', 'Save'),
          icon: 'fas fa-save',
          iconClassName: 'purple--text',
          action (context) {
            context.save()
          },
          visible (context) {
            /* return context && context.currentWebApp */
            return false
          }
        },
        {
          id: 'publish',
          label: this.$t('wdm.views.designPanel.menuItem.publish', 'Publish'),
          icon: 'fas fa-play-circle',
          iconClassName: 'purple--text',
          action (context) {
            context.publishWebStory(context.currentWebApp)
          },
          visible (context) {
            return context && context.currentWebApp
          }
        },
        {
          id: 'preview',
          label: this.$t('wdm.views.designPanel.menuItem.preview', 'Preview'),
          icon: 'fas fa-eye',
          iconClassName: 'purple--text',
          action (context) {
            context.previewWebStory(context.currentWebApp)
          },
          visible (context) {
            return context && context.currentWebApp
          }
        },
        {
          id: 'suggestions',
          label: this.$t('wdm.views.designPanel.menuItem.suggestions', 'Suggestions'),
          icon: 'far fa-lightbulb',
          className: 'publish',
          iconClassName: 'green--text',
          visible (context) {
            /* return context && context.currentWebApp */
            return false
          }
        },
        {
          id: 'template',
          label: this.$t('wdm.views.designPanel.menuItem.template', 'Template'),
          iconClassName: 'green--text',
          icon: 'fas fa-pencil-ruler',
          visible (context) {
            /* return context && context.currentWebApp */
            return false
          }
        },
        {
          id: 'widget',
          label: this.$t('wdm.views.designPanel.menuItem.widget', 'Widget'),
          iconClassName: 'green--text',
          icon: 'fas fa-swatchbook',
          visible (context) {
            /* return context && context.currentWebApp */
            return false
          }
        },
        // {
        //   id: 'chat',
        //   label: this.$t('wdm.views.designPanel.menuItem.collaboration', 'Collab'),
        //   icon: 'fas fa-users',
        //   iconClassName: 'orange--text',
        //   className: 'chat',
        //   visible (context) {
        //     return context && context.currentWebApp
        //   }
        // },
        {
          id: 'collaboration',
          label: this.$t('wdm.views.designPanel.menuItem.support', 'Support'),
          icon: 'fas fa-comment-dots',
          iconClassName: 'blue--text',
          className: 'chat'
        }
      ]
    },
    context () {
      return this
    },
    origin () {
      return `${window.location.origin}/#${this.defaultRoute}?instance=${this.instance}&token=${this.token}&isFromDesignManager=true`
    },
    filteredMenu () {
      return this.menu.filter(m => {
        return !m.visible || m.visible(this)
      })
    },
    selectedPanel: {
      get () {
        return this.currentPanel
      },
      set (value) {
        this.selectPanel(value)
      }
    },
    show () {
      return true
      // return this.office.initialized || DEBUG
    },
    canSave () {
      return this.editedDefinition && !isEqual(this.editedDefinition.item, this.editedDefinition.original)
    }
  },
  methods: {
    ...mapActions('wdm', [
      'selectPanel',
      'saveWebStory',
      'publishWebStory',
      'previewWebStory',
      'navigateToWebapp'
    ]),
    ...mapActions('office', [
      'getWebappName'
    ]),
    ...mapActions('schema-view', [
      'editDefinition'
    ]),
    onPanelClick (item) {
      if (item.action) {
        item.action(this)
      } else {
        this.selectedPanel = item.id
      }
    },
    async save () {
      if (this.canSave) {
        const item = await webAppDataSource.update(this.editedDefinition.item)
        this.editDefinition({
          schema: webAppDataSource.schema,
          item
        })
      }
      this.saveWebStory(this.currentWebApp)
    },
    openDesignManager () {
      window.open(this.origin)
    }
  },
  async mounted () {
    this.$router.push({
      query: {
        ...this.$route.query,
        hideSearchBar: true,
        hideBackArrow: true,
        hideAppSelector: true,
        hideSidebar: true,
        hidePlayerMeta: true,
        hidePlayerInfo: true,
        hideRelevant: true
      }
    })

    const name = await this.getWebappName()
    if (name) {
      const webApp = await webAppDataSource.detail(name)
      if (webApp) {
        this.navigateToWebapp(webApp)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  @import "~common-assets/scss/we";

  .webapp-header {
    position: fixed;
    top: 0;
    left: 5px;
    width: 100%;
    @extend .flex-row;
    .account-menu{
      padding-right: 15px;

    }
    .webapp-title {
      padding: 5px;
      margin-top: 5px;
    }
  }
  .action-button{
    margin: 6px 2px;
  }
  .profile-menu {
    width: 200px;
  }

  .panel-container {
    @extend .flex-row;
    @extend .full-size;
    overflow: hidden;

    .content-panel {
        margin-right: 5px;
      padding-top: 20px;
        @extend .full-size;
        display: flex;
        flex: auto;
        overflow: hidden;
    }

    .right-panel {
      height: 100%;
      width: 60px;
      margin-top: 23px;
      min-width: 60px;
      overflow: hidden;
      background: white;
      padding: 2px;

      .menu-item {
        @extend .flex-column;
        margin: 1px;
        font-size: 12px;
        cursor: pointer;
        height: 60px;
        padding: 5px 3px;
        border: 1px solid #dcdcdc;
        text-align: center;

        .v-icon {
          padding: 3px 10px;
        }
        &.disabled {
          background: rgba(144, 143, 143, 0.23);
          cursor: auto;
          .v-icon {
            color: gray !important;
          }
        }

        &.chat, &.publish {
          margin-top: 30px;
        }

        &.selected {
          .v-icon {
            color: #43a047;
          }

          color: #43a047;
          border-color: #43a047;
        }
      }
    }
  }
</style>
